import { Hidden } from '@material-ui/core';
import React from 'react';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { FormikProps } from 'formik';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { RadioTableOption } from '../../../../../../global/components/form-element/radio-table-option';
import { getCurrencyFormat } from '../../../../../../global/helpers/format-helpers';
import { LabelSettingProps } from '../../../../../../global/models/epi-model';
import { NewHireProps } from '../../../../models/arc-epi-props';
import { CoverageFormModel, CoveragePlan, DeclinedValue, DisplayLabels, PlanTypes, RelationshipCode, Tiers, TiersTypes } from '../models/coverage-models';
import { ExternalLink } from '../../../../../../global/components/external-link-modal';
import { BenefitEnrollmentStep } from '../../models/pension-enrollment-models';
import { ProgressItem } from '../../../../../../global/components/navigation/generic-stepper';
import { AccountInfoRoutes } from '../../../../../account-info/constants/routes';
import { NewHireEnrollmentRoutes } from '../../models/new-hire-routes';
import { VolumeRateValuesGroupLife } from '../coverage/volume-rate-values-group-life';
import { CoveragePageContentModel } from '../../../new-participant-enrollment/models/page-content-models';
import { GpsType } from '../../../../../../global/components/layout/layout-models';
import { WhoWillEnrollOptions } from '../../models/who-will-enroll-models';


export function getEmployeeAndSpouseLabel(tiers: Tiers, settings: NewHireProps) {
	if (!tiers?.G && !tiers?.D) {
		return settings?.empPlusOneLabel;
	}
	return settings?.empAndSpouseLabel;
}
export function isMedicalDental(planType: PlanTypes) {
	return planType === PlanTypes.M || planType === PlanTypes.D;
} 
export function getTierLabel(tier: TiersTypes, settings: NewHireProps, labelSettings: LabelSettingProps, tiers: Tiers) {
	switch (tier) {
		case TiersTypes.Single:
			return settings?.singleTierLabel;
		case TiersTypes.Family:
			return settings?.familyTierLabel;
		case TiersTypes.EmpPlusChild:
			return settings?.empPlusChildTierLabel;
		case TiersTypes.EmpPlusOne:
			return getEmployeeAndSpouseLabel(tiers, settings);
		case TiersTypes.EmpPlusMulti:
			return settings?.empPlusMultiTierLabel;
		default:
			return labelSettings?.naLabel;
	}
}



const keysTiers = ['I', 'T', 'G', 'D', 'F'];

export function getPlanOption(
    plan: CoveragePlan,
    selectedPlan: string,
    displayTier: keyof Tiers,
    availableTiers: Tiers,
    settings: NewHireProps,
    disabled: boolean,
    labelSetting: LabelSettingProps,
    displayLabels: DisplayLabels,
    formProps: FormikProps<CoverageFormModel>,
    groupId: string,
    addtionalLabels?: JSX.Element,
    showPlanCodeInName?: boolean,
): RadioTableOption {

	let additionalContent = null;

	if (availableTiers) {

		additionalContent = (
			<>
				{keysTiers?.map(k => {
					const highlighted = plan.code === selectedPlan && k === displayTier;

					if (!plan?.rates_by_tier[k]) {
						return null;
					}

					return (
						<div key={k} className={`coverage-table-row-cell tier-cell ${highlighted ? 'tier-cell-highlight' : ''}`}>
							<div className='tier-cell-inner'>
								{highlighted && <ArrowDropDownIcon className='tier-cell-indicator' />}
								<Hidden smUp>
									<p className='input-label tier-cell-item tier-cell-name'>{`${getTierLabel(k as TiersTypes, settings, labelSetting, availableTiers)}:`}</p>
								</Hidden>
								<div className='tier-cell-item tier-cell-value'>{getCurrencyFormat(plan?.rates_by_tier[k], labelSetting, true)}</div>
							</div>
						</div>
					);
				})}
			</>

		);

	}

	if (displayLabels !== DisplayLabels.MEDICALDENTAL) {
		additionalContent = VolumeRateValuesGroupLife({
			settings,
			labels: displayLabels,
			plan,
			labelSetting,
			formProps,
			groupId,
			selectedPlan,
		});
	}


	return {
		label: showPlanCodeInName ? `${plan?.code} ${plan?.name}` : plan?.name,
		value: plan?.code,
		labelSupplement: plan?.pdf && <ExternalLink linkUrl={plan?.pdf} linkIcon={<PictureAsPdfIcon />} linkText={settings?.planSummaryLinkText} />,
		additionalLabelContent: additionalContent || addtionalLabels,
		optionDisabled: disabled,
	};
}


export function getOptionDisplays(
    plans: CoveragePlan[],
    selectedPlan: string,
    displayTier: keyof Tiers,
    settings: NewHireProps,
    labelSettings: LabelSettingProps,
    declineLabel: string,
    disabled: boolean,
    displayLabels: DisplayLabels,
    formProps: FormikProps<CoverageFormModel>,
    groupId: string,
    customFieldsForRadioOption: JSX.Element,
    showPlanCodeInName: boolean,
) {

	const offerings = plans?.map(
		plan => getPlanOption(
			plan,
			selectedPlan,
			displayTier,
			plan?.rates_by_tier,
			settings,
			disabled,
			labelSettings,
			displayLabels,
			formProps,
			groupId,
			customFieldsForRadioOption,
			showPlanCodeInName,
		));

	offerings?.push({
		label: declineLabel,
		value: DeclinedValue,
	});

	return offerings;

}


export function getRelationshipLabel(code: RelationshipCode, settings: NewHireProps) {
	switch (code) {
		case RelationshipCode.Child:
			return settings?.childRelationLabel;
		case RelationshipCode.Disabled:
			return settings?.disabledChildRelationLabel;
		case RelationshipCode.StepChild:
			return settings?.stepChildRelationLabel;
		case RelationshipCode.DomesticPartner:
			return settings?.domesticPartnerRelationLabel;
		case RelationshipCode.Spouse:
			return settings?.spouseRelationLabel;
		default:
			return '';
	}
}

export function GetPageHeaderContent(step: BenefitEnrollmentStep, settings: NewHireProps, customModel: CoveragePageContentModel, isDioApprover?: boolean, isLifeEventCoverageForm?: boolean) {
	if (step === BenefitEnrollmentStep.COVERAGE) {
		if (isDioApprover){
			return {
				title: isLifeEventCoverageForm ? settings?.dioceseApproverLifeEventCoverageSectionTitle : settings?.dioceseApproverCoverageSectionTitle,
				description: isLifeEventCoverageForm ? settings?.dioceseApproverLifeEventCoverageDescription : settings?.dioceseApproverCoverageDescription,
			};
		}
		return {
			title: customModel?.pageTitle ?? settings?.coverageSectionTitle,
			description: customModel?.pageDescription ?? settings?.coverageDescription,
		};
	}

	if (step === BenefitEnrollmentStep.REVIEW && isDioApprover) {
		return {
			title: isLifeEventCoverageForm ? settings?.dioceseApproverLifeEventReviewSectionTitle : settings?.dioceseApproverReviewTitle,
			description: isLifeEventCoverageForm ? settings?.dioceseApproverLifeEventReviewDescription : settings?.dioceseApproverReviewDescription,
		};
	}
	return {
		title: customModel?.reviewTitle ?? settings?.reviewTitle,
		description: customModel?.reviewDescription ?? settings?.reviewDescription,
	};
}

export function GetPageButtonText(step: BenefitEnrollmentStep, settings: NewHireProps, isDioApprover?: boolean, isLifeEventCoverageForm?: boolean) {
	if (step === BenefitEnrollmentStep.COVERAGE) {
		return {
			prev: settings?.coveragePreviousButtonText,
			continue: settings?.coverageContinueButtonText,
		};
	}
	if ( isDioApprover && step === BenefitEnrollmentStep.REVIEW){
		return {
			prev: isLifeEventCoverageForm ? settings?.dioceseApproverLifeEventCoverageReviewBackText : settings?.dioceseApproverCoverageReviewBackText,
			continue: isLifeEventCoverageForm ? settings?.dioceseApproverLifeEventCoverageReviewSubmitText : settings?.dioceseApproverCoverageReviewSubmitText,
		};
	}
	return {
		prev: settings?.coverageReviewBackText,
		continue: settings?.coverageReviewSubmitText,
	};
}

export function GetPageHelperText(step: BenefitEnrollmentStep, settings: NewHireProps, isDioApprover?: boolean, isLifeEventCoverageForm?: boolean) {
	if (step === BenefitEnrollmentStep.COVERAGE) {
		return settings?.coverageContinueDescription;
	}
	if ( isDioApprover && step === BenefitEnrollmentStep.REVIEW){
		return isLifeEventCoverageForm ? settings?.dioceseApproverLifeEventReviewSubmitHelperText : settings?.dioceseApproverReviewSubmitHelperText;
	}
}

export function GetParticipantSelectionDescriptionText(step: BenefitEnrollmentStep, settings: NewHireProps, customModel: CoveragePageContentModel) {
	if (step === BenefitEnrollmentStep.COVERAGE) {
		return customModel?.participantSelectionDescription ?? settings?.participantSelectionDescription;
	}
}

export const getRateByCodeAndTier = (array, code, rate, enrollmentOption?: WhoWillEnrollOptions, isdeclined?: boolean) => {
	if (!array) return 0;

	if (enrollmentOption === WhoWillEnrollOptions.member && isdeclined !== true){
		return 0;
	}

	const healthPlan = array.find(plan => plan?.code === code);

	if (healthPlan && Object.prototype.hasOwnProperty.call(healthPlan.rates_by_tier, rate)) {
		return healthPlan.rates_by_tier[rate];
	}

	return 0;
};

interface Plan {
	code: string;
	name: string;
	volume?: number | null;
	rate?: number | null;
}

export function getBenefitEnrollmentSteps() {
	return [BenefitEnrollmentStep.PERSONAL_INFORMATION, BenefitEnrollmentStep.RELATIONSHIP, BenefitEnrollmentStep.PENSION, BenefitEnrollmentStep.COVERAGE, BenefitEnrollmentStep.REVIEW];
}

export function getDioApproverEnrollmentSteps(isLifeEventCoverageForm:boolean) {
	if (isLifeEventCoverageForm) {
		return [BenefitEnrollmentStep.PERSONAL_INFORMATION, BenefitEnrollmentStep.RELATIONSHIP, BenefitEnrollmentStep.COVERAGE, BenefitEnrollmentStep.REVIEW];
	}
	return [BenefitEnrollmentStep.PERSONAL_INFORMATION, BenefitEnrollmentStep.RELATIONSHIP, BenefitEnrollmentStep.EMPLOYMENT, BenefitEnrollmentStep.COVERAGE, BenefitEnrollmentStep.REVIEW];
}

export function getBenefitEnrollmentStepItems(settings: NewHireProps, onlyOtp: boolean) {
	const progressItem: ProgressItem[] = [];
	progressItem.push(
		{
			stepName: settings?.personInfoStepName,
			linkUrl: AccountInfoRoutes.PersonalInfoSegment,
			step: BenefitEnrollmentStep.PERSONAL_INFORMATION,
		},
	);
	progressItem.push(
		{
			stepName: settings?.relationshipStepName,
			linkUrl: AccountInfoRoutes.RelationshipsSegment,
			step: BenefitEnrollmentStep.RELATIONSHIP,
		},
	);

	if (!onlyOtp) {
		progressItem.push(
			{
				stepName: settings?.offeredPlanStepName,
				linkUrl: NewHireEnrollmentRoutes.Pension,
				step: BenefitEnrollmentStep.PENSION,
			},
		);
	}
	progressItem.push(
		{
			stepName: settings?.offeredCoverageStepName,
			linkUrl: NewHireEnrollmentRoutes.Coverage,
			step: BenefitEnrollmentStep.COVERAGE,
		},
	);
	progressItem.push(
		{
			stepName: settings?.reviewStepName,
			linkUrl: NewHireEnrollmentRoutes.Coverage,
			step: BenefitEnrollmentStep.REVIEW,
		},
	);

	return progressItem;
}

export function getDioApproverEnrollmentStepItems(settings: NewHireProps, isLifeEventCoverageForm: boolean) {
	const progressItem: ProgressItem[] = [];
	progressItem.push(
		{
			stepName: settings?.personInfoStepName,
			linkUrl: AccountInfoRoutes.PersonalInfoSegment,
			step: BenefitEnrollmentStep.PERSONAL_INFORMATION,
		},
	);
	progressItem.push(
		{
			stepName: settings?.relationshipStepName,
			linkUrl: AccountInfoRoutes.RelationshipsSegment,
			step: BenefitEnrollmentStep.RELATIONSHIP,
		},
	);
	if (!isLifeEventCoverageForm) {
		progressItem.push(
			{
				stepName: settings?.employmentCompensationStepName,
				linkUrl: AccountInfoRoutes.EmploymentSegment,
				step: BenefitEnrollmentStep.EMPLOYMENT,
			},
		);
	}
	progressItem.push(
		{
			stepName: settings?.offeredCoverageStepName,
			linkUrl: NewHireEnrollmentRoutes.Coverage,
			step: BenefitEnrollmentStep.COVERAGE,
		},
	);
	progressItem.push(
		{
			stepName: settings?.reviewStepName,
			linkUrl: NewHireEnrollmentRoutes.Coverage,
			step: BenefitEnrollmentStep.REVIEW,
		},
	);

	return progressItem;
}

export const getNameAndRateByCodeGroupLifeLongTernm = (array: Plan[], code: string, glRateFromApi = 0, glCustomVolume = 0, enrollmentOption: WhoWillEnrollOptions = WhoWillEnrollOptions.admin, isdeclined: boolean = false): { name: string; rate: number | null; volume: number | null } | null => {
	if (!array) return { name: '', rate: 0, volume: 0 };

	if (enrollmentOption === WhoWillEnrollOptions.member && isdeclined !== true){
		return { name: DeclinedValue, rate: 0, volume: 0 };
	}

	const plan = array.find(item => item?.code === code);
	if (plan) {
		return { name: plan?.name, rate: glRateFromApi !== 0 ? glRateFromApi : plan?.rate, volume: glCustomVolume !== 0 ? glCustomVolume : plan?.volume };
	}
	return { name: DeclinedValue, rate: glRateFromApi !== 0 ? glRateFromApi : 0, volume: 0 };
};


export function GetGpsType(step: BenefitEnrollmentStep, customModel: CoveragePageContentModel, isDioApprover: boolean, isLifeEventCoverageForm?: boolean) {

	if (isDioApprover && BenefitEnrollmentStep.COVERAGE) {
		if (isLifeEventCoverageForm){
			return GpsType.MYCPG_MIDYEAR_DIO_APPROVER_COVERAGE;
		}
		return GpsType.NEW_HIRE_COVERAGE_DIO_APPROVER;
	}

	if (isDioApprover && BenefitEnrollmentStep.REVIEW) {
		if (isLifeEventCoverageForm){
			return GpsType.MYCPG_MIDYEAR_DIO_APPROVER_REVIEW;
		}
		return GpsType.NEW_HIRE_REVIEW_DIO_APPROVER;
	}

	return step === BenefitEnrollmentStep.COVERAGE ? customModel?.gps ?? GpsType.NEW_HIRE_COVERAGE_ADMIN : customModel?.reviewGps ?? GpsType.NEW_HIRE_REVIEW_ADMIN;

	}